import { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, GeoJSON, Popup, Marker } from "react-leaflet";
import { useMap } from "react-leaflet/hooks";
import { getQuantiles, getEqualIntervals } from "./utils";
import { Typography } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo } from "@fortawesome/free-solid-svg-icons";
import { LEAFLET_CONTROL_POSITIONS } from "./const";
import { useTranslation } from "react-i18next";
import { centroid, polygon, multiPolygon } from "@turf/turf";

const GeoJsonLayer = ({ features, currentData, legend, setLabel, labelPostfix, clickHandler }) => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const map = useMap();
  const [popupContent, setPopupContent] = useState(null);
  const ranges = getEqualIntervals(
    Object.values(currentData).map((value) => value),
    legend.length
  );

  useEffect(() => {
    setTimeout(() => {
      map.fitBounds(ref.current.getBounds());
    }, 500);
  }, []);

  return (
    <GeoJSON
      ref={ref}
      data={{ features }}
      style={{ color: "#4d4d4d" }}
      onEachFeature={(feature, layer) => {
        layer.setStyle({
          weight: 1
        });
        let foundData = currentData[feature.id] ? parseFloat(currentData[feature.id]) : null;
        layer.on("click", (e) => {
          setPopupContent([
            <div>
              <strong>{e.target.feature.properties.name}</strong>: {foundData ? foundData : t("noData")}
            </div>,
            clickHandler ? clickHandler(e) : null
          ]);
        });
        if (foundData !== null) {
          const foundRangeIndex = ranges.findIndex((range) => foundData <= range.max && foundData >= range.min);
          if (foundRangeIndex !== -1) {
            layer.setStyle({
              fillColor: legend[foundRangeIndex],
              fillOpacity: 0.8
            });
          } else {
            let color;
            if (foundData < ranges[0].min) {
              color = legend[0];
            } else if (foundData > ranges[ranges.length - 1].max) {
              color = legend[ranges.length - 1];
            }
            layer.setStyle({
              fillColor: color,
              fillOpacity: 0.8
            });
          }
        } else {
          layer.setStyle({
            fillColor: "#4d4d4d",
            fillOpacity: 0.1
          });
        }
        const postfix = labelPostfix ? labelPostfix : "";
        layer.on("mouseover", () => {
          setLabel(feature.properties.name + ": " + (foundData !== null ? foundData + postfix : t("noData")));
          layer.setStyle({
            weight: 3
          });
        });
        layer.on("mouseout", () => {
          setLabel("");
          layer.setStyle({
            weight: 1
          });
        });
      }}
    >
      <Popup>{popupContent}</Popup>
    </GeoJSON>
  );
};

const LabelLayer = ({ label }) => {
  return (
    <div className={`${LEAFLET_CONTROL_POSITIONS.topRight} label-layer-container`}>
      <Typography variant="subtitle2">{label ? label : ""}</Typography>
    </div>
  );
};

const FeatureLabelLayer = ({ features }) => {
  const generateIcon = (name) => {
    return new L.divIcon({
      className: "feature-label-layer",
      html: name,
      iconAnchor: [20, 0]
    });
  };
  return features.map((feature) => {
    const icon = generateIcon(feature.properties.name);
    const centerPoint = centroid(multiPolygon(feature.geometry.coordinates));
    return <Marker key={feature.id} position={centerPoint.geometry.coordinates.reverse()} icon={icon}></Marker>;
  });
};

const LegendLayer = ({ data, legend }) => {
  const ranges = getEqualIntervals(
    Object.values(data).map((value) => value),
    legend.length
  );
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  return (
    <div
      className="legend-layer-container"
      ref={ref}
      onMouseOver={() => {
        ref.current.style.width = "170px";
        ref.current.style.height = "200px";
        ref.current.style.overflow = "auto";
        setOpen(true);
      }}
      onMouseOut={() => {
        ref.current.style.width = "34px";
        ref.current.style.height = "34px";
        ref.current.style.overflow = "hidden";
        setOpen(false);
      }}
    >
      {open ? (
        [
          <div className="legend-title">
            <strong>Legend</strong>
          </div>,
          ranges.map((range, index) => {
            return (
              <div className="legend-item">
                <div style={{ backgroundColor: legend[index] }}></div>
                <div>
                  {range.min} - {range.max}
                </div>
              </div>
            );
          })
        ]
      ) : (
        <FontAwesomeIcon icon={faCircleInfo} />
      )}
    </div>
  );
};

const VietnamNumber1Layer = () => {
  const generateIcon = (name) => {
    return new L.divIcon({
      className: "vietnam-number-one-layer-container",
      html: name,
      iconAnchor: [15, 15]
    });
  };

  return [
    <Marker key={0} position={[16.576856135859796, 112.11897181870451]} icon={generateIcon("Quần đảo Hoàng Sa")}></Marker>,
    <Marker key={1} position={[10.651007725886746, 113.84384767182405]} icon={generateIcon("Quần đảo Trường Sa")}></Marker>
  ];
};

const ThematicMap = (props) => {
  const [label, setLabel] = useState("");
  return (
    <MapContainer scrollWheelZoom={false} dragging={true}>
      <VietnamNumber1Layer />
      <LegendLayer {...props} />
      <TileLayer
        attribution={`&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>`}
        url="https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png"
      />
      {label && <LabelLayer label={label} />}
      <GeoJsonLayer {...props} setLabel={setLabel} currentData={props.data} key={props.key ? props.key : JSON.stringify(props.data)} />
      {props.showFeatureLabel && <FeatureLabelLayer {...props} />}
    </MapContainer>
  );
};

export default ThematicMap;
